import {gql} from '@apollo/client';
import React, {useMemo, useState} from 'react';

import {structuredTableActionOrigin} from 'analytics/Util';
import isArchived from 'archive/isArchived';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {MinimalStorableSummaryWithSchemaId} from 'constants/SampleConstants';
import {ProcessPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {BioentitySummary} from 'graphql-client/declarative-translations/external/RESTTypes';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import fetchSelectorsById from 'notebook/structured-tables/live-data/fetchSelectorsById';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  StructuredTable,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {getSchemaDisplayNameForCreate} from 'processes/CreateProcessUtil';
import {
  CreateProcessButtonQuery,
  CreateProcessButtonQueryVariables,
} from 'processes/tables/create-process-button/__generated/CreateProcessButtonQuery';
import {
  computeItemsShortcutDisabledState,
  isStructuredTableShortcutDisabled,
} from 'processes/tables/shortcutDisabledState';
import {userCreatableProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import bufferForUX from 'util/bufferForUX';

import {getSourceColumnsAndTypeFromStructuredTable} from '../column-mapping/getSourceColumnsAndTypeFromStructuredTable';
import {handleSelectSchema} from '../column-mapping/handleSelectSchema';

export type ProcessTaskSchema =
  CreateProcessButtonQuery['processTaskSchemasSupportingPlatformFeature'][number];

const BUTTON_TITLE = 'Create workflow tasks';

export const CREATE_PROCESS_BUTTON_QUERY = gql`
  query CreateProcessButtonQuery($platformFeature: ProcessPlatformFeatureType!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchemasSupportingPlatformFeature(platformFeature: $platformFeature) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaWithoutFlowchartConfigGraphQL
        statusLifecycle {
          ... on ProcessTaskStatusLifecycleNode {
            ...ProcessTaskStatusLifecycleGraphQL
          }
        }
      }
    }
  }
  ${getFragmentWrapper('ProcessTaskSchemaWithoutFlowchartConfig').fragment}
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
`;

interface SearchableProcessSchemaMenuProps {
  processTaskSchemas: Array<ProcessTaskSchema>;
  onSelect: (schema: ProcessTaskSchema) => void;
  style?: React.CSSProperties; // This is passed in via AbsoluteDropdownContainer
}

interface CreateProcessButtonFromTableProps {
  structuredTable: UnknownStructuredTable;
  structuredTableAdapter: UnknownStructuredTableAdapter;
  computedTable: Array<Array<EvaluatedCell>> | null;
}

interface CreateProcessButtonFromItemsProps {
  buttonStyle: ButtonStyle;
  buttonSize: ButtonSize;
  items: Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>;
  disabled: boolean;
  sourceType: string;
  // This callback aims to track user clicks.
  onClickTracking?: () => void;
}

type CreateProcessButtonProps = CreateProcessButtonFromTableProps | CreateProcessButtonFromItemsProps;

function isCreatingFromStructuredTable(
  props: CreateProcessButtonProps
): props is CreateProcessButtonFromTableProps {
  return (props as CreateProcessButtonFromTableProps).structuredTable != null;
}

function isCreatingFromItems(props: CreateProcessButtonProps): props is CreateProcessButtonFromItemsProps {
  return (props as CreateProcessButtonFromItemsProps).items != null;
}

function SearchableProcessSchemaMenu(props: SearchableProcessSchemaMenuProps): JSX.Element {
  return (
    <SearchableMenu
      height={Menu.HEIGHTS.MEDIUM}
      width={Menu.WIDTHS.MEDIUM}
      onSelect={props.onSelect}
      optionDatas={props.processTaskSchemas.map((schema) => ({
        value: schema,
        name: getSchemaDisplayNameForCreate(schema, props.processTaskSchemas),
      }))}
      dropLeft={true}
      style={props.style}
    />
  );
}

function handleSelectSchemaFromItems(
  schema: ProcessTaskSchema,
  items: Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>,
  selectorsById: SchemaFieldSelectorsDict,
  sourceType: string,
  onClickTracking?: () => void
): void {
  handleSelectSchema(
    [{columnName: 'Selected items from search', columnValues: items, hasValues: true}],
    schema,
    selectorsById,
    sourceType,
    ActionOrigin.SEARCH_EXPANDED_SEARCH_TOOLBAR,
    onClickTracking
  );
}

function handleSelectSchemaFromTable(
  schema: ProcessTaskSchema,
  structuredTable: StructuredTable<unknown, unknown>,
  computedTable: Array<Array<EvaluatedCell>> | null,
  selectorsById: SchemaFieldSelectorsDict,
  onClickTracking?: () => void
): void {
  const adapter = getStructuredTableAdapters()[structuredTable.kind];
  const {sourceColumns, sourceType} = getSourceColumnsAndTypeFromStructuredTable(
    structuredTable,
    computedTable,
    adapter
  );
  handleSelectSchema(
    sourceColumns,
    schema,
    selectorsById,
    sourceType,
    structuredTableActionOrigin(structuredTable),
    onClickTracking,
    adapter,
    structuredTable,
    computedTable
  );
}

export default function CreateProcessButton(props: CreateProcessButtonProps): JSX.Element {
  const [allSchemas, setAllSchemas] = useState<Array<ProcessTaskSchema> | null>(null);
  const [selectorsById, setSelectorsById] = useState<SchemaFieldSelectorsDict | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const structuredTableDep = isCreatingFromStructuredTable(props) ? props.structuredTable : undefined;
  const computedTableDep = isCreatingFromStructuredTable(props) ? props.computedTable : undefined;

  const hasValidSourceColumns = useMemo(() => {
    if (structuredTableDep == null) {
      return true;
    }
    const adapter = getStructuredTableAdapters()[structuredTableDep.kind];
    const {sourceColumns} = getSourceColumnsAndTypeFromStructuredTable(
      structuredTableDep,
      computedTableDep ?? null,
      adapter
    );
    return sourceColumns.length > 0;
  }, [structuredTableDep, computedTableDep]);

  let isButtonDisabled: boolean;
  if (isCreatingFromStructuredTable(props)) {
    isButtonDisabled = isStructuredTableShortcutDisabled({
      structuredTable: props.structuredTable,
      hasValidSourceColumns,
    });
  } else if (isCreatingFromItems(props)) {
    isButtonDisabled = computeItemsShortcutDisabledState({
      itemsCount: props.items.length,
      disabledFlag: props.disabled,
    });
  } else {
    isButtonDisabled = true;
  }

  const onSelect = (schema: ProcessTaskSchema, selectorsById: SchemaFieldSelectorsDict): void => {
    if (isCreatingFromStructuredTable(props)) {
      handleSelectSchemaFromTable(schema, props.structuredTable, props.computedTable, selectorsById);
    } else {
      handleSelectSchemaFromItems(
        schema,
        props.items,
        selectorsById,
        props.sourceType,
        props.onClickTracking
      );
    }
    setIsDropdownOpen(false);
  };

  const buttonStyleProps = isCreatingFromStructuredTable(props)
    ? {size: ButtonSize.MINI, buttonStyle: ButtonStyle.TERTIARY_GRAY, className: 'u-mini-spacing-left'}
    : {
        size: props.buttonSize,
        buttonStyle: props.buttonStyle,
      };

  return (
    <Dropdown
      tooltip={BUTTON_TITLE}
      menu={
        allSchemas != null && selectorsById != null ? (
          <SearchableProcessSchemaMenu
            processTaskSchemas={allSchemas}
            onSelect={(schema: ProcessTaskSchema) => {
              onSelect(schema, selectorsById);
            }}
          />
        ) : (
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM} dropLeft={true}>
            <Option isDisabled={true}>Loading schemas...</Option>
          </Menu>
        )
      }
      onToggle={async () => {
        setIsDropdownOpen(!isDropdownOpen);
        let allSchemasFetch;
        let selectorsFetch;

        // Kick off fetches
        if (allSchemas == null) {
          allSchemasFetch = bufferForUX(
            doQuerySafe<CreateProcessButtonQuery, CreateProcessButtonQueryVariables>(
              {
                query: CREATE_PROCESS_BUTTON_QUERY,
              },
              {variables: {platformFeature: ProcessPlatformFeatureType.CAN_CREATE_TASK_GROUPS}}
            )
          );
        }
        if (selectorsById == null) {
          selectorsFetch = bufferForUX(fetchSelectorsById(true));
        }

        // Process results
        if (allSchemasFetch) {
          const result = await allSchemasFetch;
          const filterByObjectsCreatable =
            BenchlingConfig.PERMISSIONS_ENABLE_PROCESS_SCHEMAS_ACCESS_POLICIES_UI;
          const instantiableSchemas = result.processTaskSchemasSupportingPlatformFeature.filter(
            (schema) =>
              !isArchived(schema) &&
              !schema.canOnlyBePartOfFlowchart &&
              userCreatableProcessTaskSchema(schema) &&
              (!filterByObjectsCreatable || schema.permissions.objectsCreatable === true)
          );
          setAllSchemas(instantiableSchemas);
        }
        if (selectorsFetch) {
          const selectors = await selectorsFetch;
          setSelectorsById(selectors);
        }
      }}
      isOpen={isDropdownOpen}
    >
      <Button
        {...buttonStyleProps}
        aria-label={BUTTON_TITLE}
        disabled={isButtonDisabled}
        icon="process"
        iconAltText={BUTTON_TITLE}
        isDropdown={true}
      />
    </Dropdown>
  );
}
